import binance from '../logos/binance.svg';
import alpaca from '../logos/alpaca.svg';

// Keyed by the `exchange` column value, lowercased. The previous map was
// `{ binance }` — key 'binance' — which never matched the stored values
// ('binance.com', 'binance.us', 'alpaca'), so no exchange ever rendered a logo.
export const EX_LOGOS = {
  'binance': binance,
  'binance.com': binance,
  'binance.us': binance,
  'alpaca': alpaca,
};

// Pretty names for the base exchange (the venue), as distinct from the user's
// own label for their connection.
export const EXCHANGE_LABELS = {
  'binance.com': 'Binance.com',
  'binance.us': 'Binance.US',
  'alpaca': 'Alpaca',
  'mexc': 'MEXC',
  'coinbase': 'Coinbase',
};

/** Raw exchange key for a connection row, lowercased ('alpaca', 'binance.us'). */
export const exchangeKey = (ex) =>
  String(ex?.exchange || ex?.exchangeType || '').toLowerCase().trim();

/** Human name of the underlying venue, e.g. 'Binance.US'. */
export const baseExchangeLabel = (ex) => {
  const key = exchangeKey(ex);
  if (!key) return '';
  return EXCHANGE_LABELS[key] || key.replace(/^\w/, (c) => c.toUpperCase());
};

/**
 * Three account types:
 *   'live'  → real money on a real exchange
 *   'paper' → real exchange API, paper-trading account (Alpaca paper, testnet)
 *   'sim'   → Cryptologics-internal simulator (no upstream API)
 *
 * The DB stores this in `account_type` (preferred) and `simulated` (legacy).
 * `simulated: true` is now exclusively the internal sim — Alpaca paper accounts
 * have `simulated: false` and `account_type: 'paper'`.
 *
 * This replaces an earlier two-state version here that only knew sim-vs-live and
 * would have labelled an Alpaca paper account "Live".
 */
export const getAccountMeta = (ex = {}) => {
  const acctType = String(ex.account_type || ex.accountType || '').toLowerCase();
  const isInternalSim = ex.simulated === true || ex.isSimulated === true || acctType === 'sim';
  const isPaper = !isInternalSim && acctType === 'paper';

  if (isInternalSim) {
    return {
      isSim: true, isPaper: false, isLive: false,
      chipLabel: 'Simulated', chipColor: 'info',
      subtitle: 'Cryptologics test account',
    };
  }
  if (isPaper) {
    const brand = String(ex.exchange || '').toLowerCase().startsWith('alpaca')
      ? 'Alpaca Paper'
      : 'Paper';
    return {
      isSim: false, isPaper: true, isLive: false,
      chipLabel: brand, chipColor: 'warning',
      subtitle: 'Paper trading at exchange',
    };
  }
  // Live. Alpaca live is branded and RED, not the generic green: the user may
  // hold an Alpaca paper and an Alpaca live connection side by side, and the
  // one difference that matters — this one trades real money — should not be
  // conveyed by a reassuring color.
  if (String(ex.exchange || '').toLowerCase().startsWith('alpaca')) {
    return {
      isSim: false, isPaper: false, isLive: true,
      chipLabel: 'Alpaca Live', chipColor: 'error',
      subtitle: 'Real money at Alpaca',
    };
  }
  return {
    isSim: false, isPaper: false, isLive: true,
    chipLabel: 'Live', chipColor: 'success',
    subtitle: 'Real Account',
  };
};

/**
 * What to show as the connection's primary label.
 *
 * Falls back to the venue name rather than a placeholder: the Alpaca connection
 * in the database has a blank `name`, which is why it rendered as "Unnamed".
 */
export const exchangeDisplayName = (ex) => {
  const name = String(ex?.name || '').trim();
  if (name) return name;
  return baseExchangeLabel(ex) || 'Unnamed';
};

/**
 * Secondary line: which venue this is and whether it trades real money.
 * e.g. "Binance.US · Simulated". Omits the venue when the primary label is
 * already the venue name (blank-name rows) so it doesn't read "Alpaca · Alpaca".
 */
export const exchangeSubtitle = (ex) => {
  const venue = baseExchangeLabel(ex);
  const mode = getAccountMeta(ex || {}).chipLabel;
  const primaryIsVenue = !String(ex?.name || '').trim();
  return primaryIsVenue ? mode : [venue, mode].filter(Boolean).join(' · ');
};

/**
 * ' · PAPER' / ' · LIVE' suffix for plain-text labels (native <option>s,
 * chips-less lists) — Alpaca connections only. Two Alpaca rows are otherwise
 * identical in a dropdown, and one of them trades real money; every place a
 * connection is picked should say which. Empty string for non-Alpaca venues
 * and for legacy rows whose account_type hasn't been backfilled yet (show
 * nothing rather than guess).
 */
export const alpacaModeSuffix = (ex) => {
  if (!exchangeKey(ex).startsWith('alpaca')) return '';
  const t = String(ex?.account_type || ex?.accountType || '').toLowerCase();
  if (t === 'paper') return ' · PAPER';
  if (t === 'live') return ' · LIVE';
  return '';
};

/** Logo for a connection, or undefined so callers can fall back to an initial. */
export const exchangeLogo = (ex) => EX_LOGOS[exchangeKey(ex)];
